import React from 'react';

import {
  Alert,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { FxSource, PropertySetupField, PropertySetupPicks, PropertySetupRow } from './excelImportModel';

type ImportPropertySetupStepProps = {
  rows: Array<PropertySetupRow>;
  picks: PropertySetupPicks;
  onToggle: (field: PropertySetupField, checked: boolean) => void;
  onFxSource: (source: FxSource) => void;
};

function LoadCell({
  row,
  isLoaded,
  onToggle,
}: {
  row: PropertySetupRow;
  isLoaded: boolean;
  onToggle: ImportPropertySetupStepProps['onToggle'];
}) {
  if (row.status === 'same') {
    return <Typography variant="body2">Same</Typography>;
  }
  if (row.status === 'note') {
    return <Typography variant="body2">{row.keeps}</Typography>;
  }
  if (row.status === 'forced') {
    return <Typography variant="body2">Comes with the currency</Typography>;
  }
  return (
    <Checkbox
      checked={isLoaded}
      inputProps={{ 'aria-label': row.label }}
      onChange={(event) => onToggle(row.field, event.target.checked)}
    />
  );
}

export default function ImportPropertySetupStep({ rows, picks, onToggle, onFxSource }: ImportPropertySetupStepProps) {
  // A kept value sits beside the workbook's in the table, so its note would only repeat it.
  const notes = rows.flatMap(({ note, keeps }) => (note && keeps !== 'Kept' ? [note] : []));
  const isLoaded = (row: PropertySetupRow) => row.status === 'forced' || picks.fields[row.field];
  return (
    <>
      <Typography color="text.secondary" variant="body2">
        Tick what to take from the workbook. The currency picked here decides whether net prices, vendor revenue
        overrides, extra guest surcharges and RRP load.
      </Typography>
      <Table size="small" sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Field</TableCell>
            <TableCell>In this session</TableCell>
            <TableCell>In the workbook</TableCell>
            <TableCell>Load</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            // The FX choice sits on its own line under the rate, read as part of the same row.
            const joined = row.fxOptions ? { borderBottom: 'none' } : {};
            return (
              <React.Fragment key={row.field}>
                <TableRow>
                  <TableCell sx={joined}>{row.label}</TableCell>
                  <TableCell sx={joined}>{row.session}</TableCell>
                  <TableCell sx={joined}>{row.workbook}</TableCell>
                  <TableCell
                    padding={row.status === 'differs' ? 'checkbox' : 'normal'}
                    sx={{ whiteSpace: 'nowrap', ...joined }}
                  >
                    <LoadCell isLoaded={isLoaded(row)} onToggle={onToggle} row={row} />
                  </TableCell>
                </TableRow>
                {row.fxOptions && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ pt: 0 }}>
                      <RadioGroup
                        aria-label="FX rate to use"
                        name="fxSource"
                        onChange={(_, value) => {
                          const picked = row.fxOptions.find(({ source }) => source === value);
                          if (picked) {
                            onFxSource(picked.source);
                          }
                        }}
                        row
                        value={row.fxSource}
                      >
                        {row.fxOptions.map(({ source, rate, label }) => (
                          <FormControlLabel
                            control={<Radio size="small" />}
                            disabled={rate === null || !isLoaded(row)}
                            key={source}
                            label={label}
                            slotProps={{ typography: { variant: 'body2' } }}
                            value={source}
                          />
                        ))}
                      </RadioGroup>
                      {row.fxDifference && (
                        <Typography color="text.secondary" variant="body2">
                          {row.fxDifference}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      {notes.map((note) => (
        <Alert key={note} severity="info" sx={{ mt: 2 }}>
          {note}
        </Alert>
      ))}
    </>
  );
}
